import { FormNode, AnswersNode } from '../types/utils/findAnswersByTitles';
import { JsonSchema } from '../types/utils/createUI';

/**
 * Finds question IDs by their titles in the form schema
 * @param formJson The JSON schema of the form
 * @param titles Array of question titles to find
 * @returns A record mapping titles to their corresponding question IDs
 */
export function findQuestionIdsByTitles(formJson: JsonSchema | FormNode | null, titles: string[]): Record<string, string> {
    if (!formJson) return {};
    const ids: Record<string, string> = {};

    // Using a recursive function with type assertions for safety
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const traverse = (node: any, path: string = ''): void => {
        if (!node) return;

         // Check if the current node matches any of our titles (case-insensitive)
        if (typeof node.title === 'string') {
            const nodeTitle = node.title.trim().toLowerCase();
            const matchingTitle = titles.find(
                (title: string) => title.trim().toLowerCase() === nodeTitle
            );
            
            if (matchingTitle) {
                // Get the ID or use the path as a fallback
                const id = typeof node.id === 'string' ? node.id : path;
                // Use the ORIGINAL input title as the key, not the node title from the schema
                // This ensures the consumer gets back the exact keys they passed in
                ids[matchingTitle] = id;
            }
        }
        // Traverse properties
        if (node.properties && typeof node.properties === 'object') {
            for (const key in node.properties) {
                traverse(node.properties[key], path ? `${path}.${key}` : key);
            }
        }
        
        // Traverse array items
        if (node.items) {
            if (Array.isArray(node.items)) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                node.items.forEach((item: any, index: number) => {
                    traverse(item, `${path}[${index}]`);
                });
            } else {
                // Handle the case where items is a single object
                traverse(node.items, `${path}[]`);
            }
        }

        // Special case for oneOf structures
        if (Array.isArray(node.oneOf)) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            node.oneOf.forEach((item: any, index: number) => {
                traverse(item, `${path}.oneOf[${index}]`);
            });
        }

        // Check for nested dependencies
        if (node.dependencies && typeof node.dependencies === 'object') {
            for (const key in node.dependencies) {
                traverse(node.dependencies[key], `${path}.dependencies.${key}`);
            }
        }
    };

    traverse(formJson);
    return ids;
}

/**
 * Extracts answer values for question IDs from the form data
 * @param answersJson The form data containing answers
 * @param questionIds Record mapping titles to question IDs
 * @returns A record mapping titles to their answer values
 */
export function findAnswersByQuestionIds(answersJson: AnswersNode, questionIds: Record<string, string>): Record<string, unknown> {
    if (!answersJson || !questionIds || Object.keys(questionIds).length === 0) {
        return {};
    }
    
    const answers: Record<string, unknown> = {};
    const idList = Object.values(questionIds);
    
    // Create a map of titles to IDs with lowercase keys for case-insensitive matching
    const titleToIdMap: Record<string, string[]> = {};
    for (const [title, id] of Object.entries(questionIds)) {
        const lowerTitle = title.toLowerCase();
        if (!titleToIdMap[lowerTitle]) {
            titleToIdMap[lowerTitle] = [];
        }
        titleToIdMap[lowerTitle].push(id);
    }

    // Handle direct matches at the top level
    for (const [key, value] of Object.entries(answersJson)) {
        if (idList.includes(key)) {
            // Find the original title that maps to this ID
            const title = Object.keys(questionIds).find(t => questionIds[t] === key);
            if (title) {
                answers[title] = value;
            }
        }
    }

    // If we haven't found all answers, try a deeper search
    if (Object.keys(answers).length < Object.keys(questionIds).length) {
        // Define the deep search function outside the if block to avoid strict mode issues
        const deepSearch = (obj: Record<string, unknown>, currentPath: string = ''): void => {
            if (!obj || typeof obj !== 'object') return;

            for (const [key, value] of Object.entries(obj)) {
                const fullPath = currentPath ? `${currentPath}.${key}` : key;
                
                if (idList.includes(fullPath)) {
                    const title = Object.keys(questionIds).find(t => questionIds[t] === fullPath);
                    if (title && !(title in answers)) {
                        answers[title] = value;
                    }
                }
                
                if (idList.includes(key)) {
                    const title = Object.keys(questionIds).find(t => questionIds[t] === key);
                    if (title && !(title in answers)) {
                        answers[title] = value;
                    }
                }
                
                if (typeof value === 'object' && value !== null) {
                    deepSearch(value as Record<string, unknown>, fullPath);
                }
            }
        };
        
        deepSearch(answersJson);
    }

    return answers;
}
